import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Checkbox } from "@progress/kendo-react-inputs";
import IDCell from "./Cell/IdCell";
import { useComponentProperties } from "../../../../Hooks/Display/useComponentProperties";
import { DraggableRow } from "../../AttributeTable/Grid/Row/DraggableRow";
import { useContext, useState } from "react";
import { reorderData } from "./utils";
import useComponents, { useSelectedComponent } from "../../../../Hooks/Display/useComponents";
import { useTranslation } from "react-i18next";
import EditTabDialog from "./EditTabDialog";
import RemoveDialog from "../../../Shared/Remove/RemoveDialog";
import { deleteComponent } from "../../../../API/Display/Display";
import { useSelectedPage } from "../../../../Hooks/Display/usePages";
import { DELETE_COMPONENT_TO_FLAT_LIST } from "../../../../Context/Display/Components/ComponentsContextEvents";
import GenericTranslationDialog from "../../../Entities/GenericTranslationDialog";
import { usePropsStoreValue } from "../../../../Hooks/PropStore/usePropsStore";
import { GlobalContext } from "../../../../Renderers/GlobalContext";

function ChildrenTabManagementGrid() {

    const { t } = useTranslation("display/components/tab_container", { useSuspense: false });

    const { subscription } = useContext(GlobalContext).state

    const selectedComponent = useSelectedComponent();
    const selectedPage = useSelectedPage();
    const [properties, updateGeneralProperty] = useComponentProperties();

    const [activeItem, setActiveItem] = useState();
    const [openEditTabDialog, setOpenEditTabDialog] = useState(false);
    const [selectedTab, setSelectedTab] = useState({});
    const [showDeleteTabDialog, setShowDeleteTabDialog] = useState(false);
    const [deletingTab, setDeletingTab] = useState(null);
    const [, dispatch] = useComponents()

    const [inEditId, setInEditId] = useState(null);
    const [showLabelTranslationDialog, setShowLabelTranslationDialog] = useState(false);
    const [translationData, setTranslationData] = useState(null);
    const project = usePropsStoreValue("project");

    function toggleHidden(id) {
        const updatedChildren = properties.children.map((item) => {
            if (item.id === id) {
                return {
                    ...item,
                    hidden: !item.hidden,
                    children: item.children ? toggleHideChildren(item.children, !item.hidden) : item.children
                }
            }
            return item;
        });
        updateGeneralProperty("children", structuredClone(updatedChildren));
    }

    function toggleHideChildren(components, newHiddenState) {
        const updatedChildren = components.map((item) => {
            return {
                ...item,
                hidden: newHiddenState,
                children: item.children ? toggleHideChildren(item.children, newHiddenState) : item.children
            };
        });
        return updatedChildren;
    }

    function dragStart(dataItem) {
        setActiveItem(dataItem);
    }

    function endDrag(dataItem, direction) {
        const reorderedData = reorderData(
            properties.children, activeItem,
            dataItem, direction
        );
        updateGeneralProperty("children", structuredClone(reorderedData));
    }

    async function deleteTab() {
        if (!deletingTab) return;
        await deleteComponent(selectedPage._id, deletingTab.id);
        const updatedChildren = properties.children.filter((item) => item.id !== deletingTab.id);
        updateGeneralProperty("children", structuredClone(updatedChildren));
        dispatch({ type: DELETE_COMPONENT_TO_FLAT_LIST, payload: deletingTab.id })
        dispatch({ type: DELETE_COMPONENT_TO_FLAT_LIST, payload: deletingTab.children[0].id })
        setShowDeleteTabDialog(false);
        setDeletingTab(null);
    }

    function setLabelTranslation(data) {
        const updatedChildren = properties.children.map((item) => {
            if (item.id === inEditId) {
                return {
                    ...item,
                    languages: data
                }
            }
            return item;
        });
        updateGeneralProperty("children", structuredClone(updatedChildren));
        setTranslationData(null);
        setInEditId(null);
        setShowLabelTranslationDialog(false);
    }

    return (
        <>
            {openEditTabDialog && (
                <EditTabDialog
                    onClose={() => {
                        setSelectedTab({});
                        setOpenEditTabDialog(false)
                    }}
                    selectedTab={selectedTab}
                />
            )}
            {showDeleteTabDialog && (
                <RemoveDialog
                    Title={t("removeDialog.title")}
                    toggleDeleteDialog={() => {
                        setShowDeleteTabDialog(false);
                        setDeletingTab(null);
                    }}
                    message={t("removeDialog.message", { name: deletingTab.componentName })}
                    onDelete={deleteTab}
                    onCancel={() => {
                        setShowDeleteTabDialog(false);
                        setDeletingTab(null);
                    }}
                    cancelButtonText={t("removeDialog.buttons.cancelButtonText")}
                    deleteButtonText={t("removeDialog.buttons.deleteButtonText")}
                />
            )}
            {showLabelTranslationDialog && (
                <GenericTranslationDialog
                    onClose={() => { setShowLabelTranslationDialog(false) }}
                    onSave={setLabelTranslation}
                    languages={translationData}
                    project={project}
                    fields={["label"]}
                />
            )}
            <Grid
                data={properties.children.map((item, index) => ({
                    ...item,
                    _id: index + 1,
                    selected: item.id === 0
                }))}
                selectedField="selected"
                className="h-90"
                rowRender={(row, rowProps) => (
                    <DraggableRow
                        elementProps={row.props} {...rowProps}
                        dragStart={dragStart}
                        reorder={endDrag}
                    />
                )}
            >
                <GridColumn field="_id" title="#" width={50} cell={IDCell} />
                <GridColumn field="componentName" title={t("grid.tab")} />
                <GridColumn field="label" title={t("grid.label")} />
                <GridColumn title={t("grid.show")} cell={(props) => (
                    <td>
                        <Checkbox
                            disabled={selectedComponent.hidden}
                            onClick={() => toggleHidden(props.dataItem.id)}
                            checked={!props.dataItem.hidden}
                        />
                    </td>
                )} />
                <GridColumn field="" title="" cell={(props) => (
                    <td>
                        <div className="float-right m-2 font-size-2">
                            {subscription.numOfLanguages > 1 &&
                                <i
                                    onClick={() => {
                                        setInEditId(props.dataItem.id);
                                        setTranslationData(props.dataItem.languages);
                                        setShowLabelTranslationDialog(true);
                                    }}
                                    title="Language Translation"
                                    className="fas fa-language cursor-pointer"
                                />
                            }
                            <i
                                onClick={() => {
                                    setSelectedTab(props.dataItem);
                                    setOpenEditTabDialog(true)
                                }}
                                title="Edit Tab"
                                className="ms-4 fas fa-pencil text-secondary cursor-pointer"
                            ></i>
                            <i
                                onClick={() => {
                                    setDeletingTab(props.dataItem);
                                    setShowDeleteTabDialog(true);
                                }}
                                title="Delete Tab"
                                className="fas fa-trash-alt text-danger ms-4 cursor-pointer"
                            ></i>
                        </div>
                    </td>
                )} />
            </Grid>
        </>
    )
}

export default ChildrenTabManagementGrid;